import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { FaChevronDown } from 'react-icons/fa';

const CategoryNav = ({ isMobile }) => {
  const navigate = useNavigate();

  const handleCategoryClick = (categoryName) => {
    navigate(`/categories?category=${categoryName}`);
  };

  return (
    <NavContainer>
      <NavContent>
        <NavList $isMobile={isMobile}>
          <NavItem>
            <NavLink to="/categories">
              All Products
            </NavLink>
          </NavItem>
          
          <NavItem>
            <NavLink 
              to="/categories?category=Silver"
              onClick={(e) => {
                e.preventDefault();
                handleCategoryClick('Silver');
              }}
            >
              Silver
            </NavLink>
          </NavItem>

          <NavItem>
            <NavLink 
              to="/categories?category=Copper"
              onClick={(e) => {
                e.preventDefault();
                handleCategoryClick('Copper');
              }}
            >
              Copper
            </NavLink>
          </NavItem>

          <NavItem>
            <NavLink 
              to="/categories?category=Ancient/Shipwreck"
              onClick={(e) => {
                e.preventDefault();
                handleCategoryClick('Ancient/Shipwreck');
              }}
            >
              Ancient/Shipwreck
            </NavLink>
          </NavItem>

          <NavItem>
            <TreasureHuntLink to="/treasure-hunt">
              🪙 Treasure Hunt
            </TreasureHuntLink>
          </NavItem>

          <NavItem>
            <NavLink to="/about">
              About Us
            </NavLink>
          </NavItem>

          <NavItem>
            <NavLink to="/contact">
              Contact
            </NavLink>
          </NavItem>

          <NavItem>
            <NavLink to="/blog">
              Blog
            </NavLink>
          </NavItem>
        </NavList>
      </NavContent>
    </NavContainer>
  );
};

const NavContainer = styled.nav`
  width: 100%;
`;

const NavContent = styled.div`
  width: 100%;
  
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    
    /* Hide scrollbar but keep functionality */
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const NavList = styled.ul`
  display: flex;
  align-items: center;
  justify-content: center;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 0.25rem;
  flex-direction: ${({ $isMobile }) => $isMobile ? 'column' : 'row'};

  @media (max-width: 768px) {
    justify-content: flex-start;
    gap: 0;
  }
`;

const NavItem = styled.li`
  position: relative;
`;

const NavLink = styled(Link)`
  display: block;
  padding: 0.5rem 0.75rem;
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all ${({ theme }) => theme.transitions.fast};
  white-space: nowrap;
  position: relative;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  
  &:hover {
    color: ${({ theme }) => theme.colors.metallic.goldLight};
    background: rgba(212, 175, 55, 0.1);
  }

  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.85rem;
  }
`;

const DropdownTrigger = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  position: relative;
  border-radius: ${({ theme }) => theme.borderRadius.sm};

  svg {
    font-size: 0.7rem;
    transition: transform ${({ theme }) => theme.transitions.fast};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.metallic.goldLight};
    background: rgba(212, 175, 55, 0.1);
    
    svg {
      transform: rotate(180deg);
    }
  }

  &:hover > div {
    display: block;
  }

  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.85rem;
  }
`;

const DropdownMenu = styled.div`
  display: none;
  position: absolute;
  top: 100%;
  right: 0;
  min-width: 200px;
  background: ${({ theme }) => theme.colors.background.card};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.metallic.gold};
  overflow: hidden;
  margin-top: 0.5rem;
  z-index: 100;
`;

const DropdownItem = styled(Link)`
  display: block;
  padding: 0.75rem 1rem;
  color: ${({ theme }) => theme.colors.text.dark};
  text-decoration: none;
  font-size: 0.9rem;
  transition: all ${({ theme }) => theme.transitions.fast};
  border-left: 3px solid transparent;

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.light};
    border-left-color: ${({ theme }) => theme.colors.metallic.gold};
    padding-left: 1.25rem;
  }
`;

const DropdownDivider = styled.div`
  height: 1px;
  background-color: #eee;
  margin: 0.25rem 0;
`;

const TreasureHuntLink = styled(Link)`
  display: block;
  padding: 0.5rem 0.75rem;
  color: #D4AF37;
  text-decoration: none;
  font-weight: 700;
  font-size: 0.9rem;
  transition: all 0.2s;
  white-space: nowrap;
  border-radius: 4px;
  background: linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(244, 208, 63, 0.1) 100%);
  border: 1px solid rgba(212, 175, 55, 0.3);
  
  &:hover {
    color: #1a1a2e;
    background: linear-gradient(135deg, #D4AF37 0%, #F4D03F 100%);
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.85rem;
  }
`;

export default CategoryNav;
